import "./SearchBar.scss";

import cx from "classnames";
import ButtonIcon from "components/ButtonIcon/ButtonIcon.tsx";
import LabelPill from "components/LabelPill/LabelPill";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toggleSearchFilter } from "reducers/main";
import { useQuery } from "utils/hooks";
import { useDispatch, useSelector } from "utils/redux.ts";
import { GLOBAL_SEARCH_PARAM, useUrlStore } from "utils/useUrlStore.ts";

import SearchFilterPanel from "./SearchFilterPanel.tsx";

const DEBOUNCE_IN_MS = 500;

type SearchBarPropTypes = {
  filters?: string[];
  isHidden?: boolean;
  override?: boolean;
  variant?: "header" | "documents";
  showFilterButton?: boolean;
  filterType?:
    | "files"
    | "claims"
    | "rres"
    | "your-tasks"
    | "operations"
    | "documents";
};

const SearchBar = ({
  filters = [],
  isHidden = false,
  override = false,
  variant = "header",
  showFilterButton = true,
  filterType,
}: SearchBarPropTypes) => {
  const ref = useRef<HTMLDivElement>(null);
  const query = useQuery();
  const location = useLocation();
  const dispatch = useDispatch();
  const { setSearchTerm, clearSearchTerm, getFilters, clearFilters } =
    useUrlStore();
  const [inputValue, setInputValue] = useState("");
  const searchParam = query.get(GLOBAL_SEARCH_PARAM);
  const isSearchFilterActive = useSelector(
    (state) => state.main.searchFilterActive,
  );

  // check if there are any active filters (excluding search term and navigation params)
  const activeFilters = getFilters();
  // on overview page we want to make sure that file_id isn't considered an active filter
  const navigationParams = ["file_id"];
  const filterKeys = Object.keys(activeFilters).filter(
    (key) => !navigationParams.includes(key),
  );
  const hasActiveFilters = filterKeys.length > 0;

  const searchBarEnabled =
    location.pathname === "" ||
    location.pathname === "/" ||
    location.pathname === "/section-111/claims" ||
    location.pathname === "/your-tasks" ||
    location.pathname === "/section-111/rres" ||
    location.pathname === "/operations/files" ||
    location.pathname === "/operations/intake" ||
    override;

  // determine filterType: use prop if provided, otherwise fall back to URL-based logic
  const getFilterTypeFromPath = ():
    | "files"
    | "claims"
    | "rres"
    | "your-tasks"
    | "operations"
    | "documents" => {
    // since not an exact url, we need to check the variant prop
    if (variant === "documents") return "documents";

    // map specific paths to filter types
    if (location.pathname.includes("/section-111/claims")) return "claims";
    if (location.pathname.includes("/section-111/rres")) return "rres";
    if (location.pathname.includes("/your-tasks")) return "your-tasks";
    if (location.pathname.includes("/operations/files")) return "files";
    if (location.pathname.includes("/operations/intake")) return "files"; // use same filters as files

    // default fallback
    return "claims";
  };

  const finalFilterType = filterType || getFilterTypeFromPath();

  useEffect(() => {
    // close filter when clicking outside
    if (isSearchFilterActive) {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as HTMLElement;

        // check if click is within the search filter panel or its portaled elements.
        // the date pickers render their calendar into #datepicker-portal on
        // document.body (see DatePicker's portalId), so a click on a day sits
        // outside the panel in the DOM and would otherwise dismiss it.
        const isWithinSearchFilterPanel =
          target.closest("#search-filter-panel") ||
          target.closest('[class*="search-filter-react-select"]') ||
          target.closest("#datepicker-portal") ||
          target.closest(".react-datepicker");

        if (
          ref.current &&
          !ref.current.contains(target as Node) &&
          !target.classList.contains("filter_button_icon") &&
          !isWithinSearchFilterPanel
        ) {
          dispatch(toggleSearchFilter());
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isSearchFilterActive, dispatch]);

  useEffect(() => {
    setInputValue(searchParam || "");
  }, [searchParam]);

  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value);
      }, DEBOUNCE_IN_MS),
    [setSearchTerm],
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      debouncedSetSearchTerm(newValue);
    },
    [debouncedSetSearchTerm],
  );

  const handleClearSearch = useCallback(() => {
    clearSearchTerm();
    clearFilters();
    setInputValue("");
    debouncedSetSearchTerm.cancel();
  }, [clearSearchTerm, clearFilters, debouncedSetSearchTerm]);

  useEffect(() => {
    return () => {
      debouncedSetSearchTerm.cancel();
    };
  }, [debouncedSetSearchTerm]);

  return (
    <>
      <div className={cx("SearchBar", { "SearchBar--Hidden": isHidden })}>
        <div
          className={cx("SearchBar__SearchBar__Search", {
            "SearchBar__SearchBar__Search--without-filter": !showFilterButton,
          })}
        >
          <ButtonIcon onClick={() => {}}>search</ButtonIcon>
          <input
            className="SearchBar__SearchBar__Search__SearchInput"
            value={inputValue}
            onChange={handleSearchChange}
          />
          <div className="SearchBar__SearchBar__Search__Filters">
            {filters.map((filter) => (
              <LabelPill key={filter} closable>
                {filter}
              </LabelPill>
            ))}
          </div>
          <ButtonIcon
            className={cx({
              "clear_button--has-filters": hasActiveFilters,
            })}
            onClick={handleClearSearch}
          >
            close
          </ButtonIcon>
          {showFilterButton && (
            <ButtonIcon
              className={cx("filter_button_icon", {
                "filter_button_icon--active": hasActiveFilters,
              })}
              disabled={!searchBarEnabled}
              onClick={() => {
                dispatch(toggleSearchFilter());
              }}
            >
              filter_list
            </ButtonIcon>
          )}
        </div>
      </div>
      {showFilterButton && (
        <SearchFilterPanel
          ref={ref}
          variant={variant}
          filterType={finalFilterType}
        />
      )}
    </>
  );
};

export default SearchBar;
